import type { ContactImportClassificationCounts } from "@multi-chat/shared";

export type ImportOutcomeTone =
  | "pending"
  | "unmapped"
  | "empty"
  | "exceeded"
  | "exact"
  | "fits"
  | "unlimited";

export type ClassificationLineId =
  | "restored"
  | "duplicateInFile"
  | "duplicateExisting"
  | "unreadable"
  | "unlinkedProfile";

type CapacitySnapshot = {
  contactsRequiredByImport: number;
  contactsLimit: number | null;
  remainingCapacity: number | null;
  fitsContactLimit: boolean;
  classification?: ContactImportClassificationCounts | null;
};

export function importOutcomeTone(
  capacity: CapacitySnapshot | null,
  classificationPending: boolean,
): ImportOutcomeTone {
  if (classificationPending) return "pending";
  if (capacity == null) return "unmapped";
  if (capacity.contactsRequiredByImport === 0) return "empty";
  if (capacity.contactsLimit == null || capacity.remainingCapacity == null) return "unlimited";
  if (
    capacity.fitsContactLimit === false &&
    capacity.contactsRequiredByImport > capacity.remainingCapacity
  ) {
    return "exceeded";
  }
  if (capacity.contactsRequiredByImport === capacity.remainingCapacity) return "exact";
  return "fits";
}

export function classificationDisclosureLines(
  classification: ContactImportClassificationCounts,
  autoLinkExactPhoneMatches: boolean,
): { id: ClassificationLineId; count: number }[] {
  const lines: { id: ClassificationLineId; count: number }[] = [
    { id: "restored", count: classification.restored },
    { id: "duplicateInFile", count: classification.duplicateInFile },
    { id: "duplicateExisting", count: classification.duplicateExisting },
    {
      id: "unreadable",
      count:
        classification.invalid +
        classification.ambiguousDeleted +
        classification.ambiguousProfile,
    },
    {
      id: "unlinkedProfile",
      count: autoLinkExactPhoneMatches ? 0 : classification.exactUnlinked,
    },
  ];
  return lines.filter((line) => line.count > 0);
}

/** Places left after this import. Null when the organization limit is unlimited. */
export function placesLeftAfterImport(capacity: {
  remainingCapacity: number | null;
  contactsRequiredByImport: number;
}): number | null {
  if (capacity.remainingCapacity == null) return null;
  return Math.max(0, capacity.remainingCapacity - capacity.contactsRequiredByImport);
}

/** Slots this import still needs beyond the current remaining capacity. Null when unlimited. */
export function contactImportShortfall(capacity: {
  remainingCapacity: number | null;
  contactsRequiredByImport: number;
}): number | null {
  if (capacity.remainingCapacity == null) return null;
  return capacity.contactsRequiredByImport - capacity.remainingCapacity;
}

export function contactsNotAddedCount(
  classification: ContactImportClassificationCounts,
  autoLinkExactPhoneMatches: boolean,
): number {
  const unreadable =
    classification.invalid + classification.ambiguousDeleted + classification.ambiguousProfile;
  const unlinked = autoLinkExactPhoneMatches ? 0 : classification.exactUnlinked;
  return classification.duplicate + unreadable + unlinked;
}
